/**
 * Identidad del usuario **en desarrollo**.
 *
 * En producción la identidad la pone Cloudflare Access: pone el header
 * `Cf-Access-Authenticated-User-Email` antes de que el request llegue al Worker,
 * y el Worker resuelve el rol buscando ese email en la tabla `users`. El front no
 * participa y no tiene que hacer nada.
 *
 * Local no hay Access, así que sin esto **todo** devuelve 403 "Falta la identidad
 * de Cloudflare Access" y el panel entero muestra la pantalla de sin acceso — que
 * es exactamente lo que pasaba antes de agregar este archivo. El Worker ya traía
 * la contraparte (`X-Dev-User-Email`, que solo lee cuando `ENVIRONMENT` es
 * `development`); lo que faltaba era alguien que lo mandara.
 *
 * Es un bypass de identidad, así que las dos mitades están cerradas:
 *
 *  - **El front** lo compila solo en dev. `import.meta.env.DEV` es una constante
 *    que Vite reemplaza en build, así que en el bundle de producción este código
 *    desaparece entero por tree-shaking — no queda ni el nombre del header.
 *  - **El Worker** lo ignora salvo que `ENVIRONMENT` sea `development`, y tiene un
 *    test que lo verifica.
 *
 * Hacen falta las dos: si solo una estuviera, cualquiera se haría admin mandando
 * un header.
 */

/** El mismo nombre que lee `apps/api/src/middleware/auth.ts`. */
const DEV_EMAIL_HEADER = 'X-Dev-User-Email'

const DEV_EMAIL_KEY = 'ops:dev-email'

/** Parámetro de URL para cambiar de identidad: `?dev-email=lucia@…`. */
const DEV_EMAIL_PARAM = 'dev-email'

/**
 * A quién se le pide el panel cuando nadie eligió. Es el admin del seed: sin un
 * default, un clon recién levantado abriría en la pantalla de sin acceso y
 * parecería que el arranque falló.
 */
export const DEV_EMAIL_POR_DEFECTO = 'seba@laburen.com'

function leerDeLaUrl(): string | null {
  if (typeof window === 'undefined') return null
  const valor = new URLSearchParams(window.location.search).get(DEV_EMAIL_PARAM)
  if (valor === null) return null
  const limpio = valor.trim().toLowerCase()
  return limpio === '' ? null : limpio
}

function almacenamiento(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    // Safari en modo privado tira al tocar localStorage.
    return null
  }
}

/**
 * Con qué email se está pidiendo el panel en dev.
 *
 * El parámetro de URL gana y **queda pegado**: se guarda en `localStorage` para
 * que la identidad sobreviva a navegar entre rutas, que es lo que hace útil
 * `?dev-email=…` para probar un rol de punta a punta.
 */
export function devEmail(): string {
  const deLaUrl = leerDeLaUrl()
  const store = almacenamiento()

  if (deLaUrl !== null) {
    store?.setItem(DEV_EMAIL_KEY, deLaUrl)
    return deLaUrl
  }

  return store?.getItem(DEV_EMAIL_KEY) ?? DEV_EMAIL_POR_DEFECTO
}

/**
 * Headers de identidad de cada request. Vacío en producción, donde la pone
 * Cloudflare Access.
 */
export function headersDeIdentidad(): Record<string, string> {
  if (!import.meta.env.DEV) return {}
  return { [DEV_EMAIL_HEADER]: devEmail() }
}
